import React from 'react';
import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defaultRenderProductLink, type RenderProductLink } from './routing';
import {
  Search,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  X,
  Columns3,
  Bookmark,
  Download,
  Settings2,
  Filter,
  ArrowUpDown,
  AlertCircle,
  Trash2,
  PencilLine,
  Percent,
  Handshake,
  Lock,
  ShieldCheck,
} from './icons';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cx as cn,
} from './ui';
import { formatCurrency } from './lib/store';
import { countries } from '../../utils/countriesWithCodes';
import { GlobalContext } from '../../providers/GlobalProvider';
import {
  bulkDeleteImportDatabaseProducts,
  bulkUpdateImportTariffExclusion,
  bulkUpdateImportFtas,
  bulkUpdateImportProductDetails,
  createImportDatabaseSavedView,
  deleteImportDatabaseSavedView,
  fetchImportDatabaseSavedViews,
  fetchImportProductCategories,
  fetchImportProductChildren,
  fetchBulkImportTariffExclusionOptions,
  fetchBulkImportFtaOptions,
  normalizeHtsForSearch,
  toImportProduct,
  updateImportDatabaseSavedView,
} from './lib/import-service';
import type {
  ImportDatabaseSavedView,
  ImportDatabaseSavedViewConfiguration,
  ImportProductCategory,
  ImportProductDetailsUpdate,
  ImportBulkTariffExclusionOption,
  ImportBulkFtaOptionsResult,
  ImportSavedViewColumn,
} from './lib/import-service';
import {
  SaveImportViewDialog,
  type ImportSavedViewSummaryItem,
} from './SaveImportViewDialog';
import { toast } from './lib/toast';
import { useImportHubReadOnly } from './lib/access';
import {
  IMPORT_DATABASES,
  IMPORT_ENTITIES,
  PGA_AGENCIES,
} from './lib/import-data';
import { PGA_PROGRAMS, pgaProgramInfo } from './lib/pga-programs';
import type {
  ImportIssueType,
  ImportProduct,
  PgaAgency,
  ImportRisk,
} from './lib/import-types';

const RISKS: ImportRisk[] = [
  'Not Assessed',
  'No Matches',
  'Low',
  'Medium',
  'High',
  'Critical',
];
const FTA_OPTS = ['eligible', 'ineligible'] as const;
const ADD_BUCKETS = [
  { value: '0', label: '0%' },
  { value: '0-10', label: '>0–10%' },
  { value: '10-25', label: '>10–25%' },
  { value: '25+', label: '>25%' },
];
const SCORE_BUCKETS = [
  { value: 'low', label: 'Low (<65)' },
  { value: 'mid', label: 'Mid (65–84)' },
  { value: 'high', label: 'High (≥85)' },
];
const VALUE_BUCKETS = [
  { value: '0', label: '$0' },
  { value: '0-1k', label: 'Over $0–$1,000' },
  { value: '1k-10k', label: '$1,000–$10,000' },
  { value: '10k-100k', label: '$10,000–$100,000' },
  { value: '100k+', label: 'Over $100,000' },
];
const CLASS_HEALTH = [
  { value: 'missing-hts', label: 'Missing HTS' },
  { value: 'invalid-hts', label: 'Invalid HTS' },
  { value: 'missing-coo', label: 'Missing COO' },
];
const ISSUE_FILTERS: Array<{ value: ImportIssueType; label: string }> = [
  { value: 'hts_review_required', label: 'HTS review required' },
  { value: 'tariff_change', label: 'Tariff changed' },
  { value: 'analysis_required', label: 'Analysis required' },
  { value: 'compliance_flag', label: 'Compliance flag' },
];
const HTS_RE = /^\d{4}\.\d{2}(?:\.\d{2})?(?:\.\d{2})?$/;

// Screened products carry a server-derived htsInvalid. Otherwise accept the
// valid 6/8/10-digit dotted tariff forms supported by the product service.
function isHtsInvalid(p: ImportProduct): boolean {
  if (!p.htsCode) return false;
  if (p.htsInvalid !== undefined) return p.htsInvalid;
  return !HTS_RE.test(p.htsCode);
}

export interface ImportTableFilters {
  q?: string;
  risk?: string[];
  pga?: string[];
  tariff?: string[];
  value?: string[];
  fta?: string[];
  origin?: string[];
  database?: string[];
  addDuty?: string[];
  score?: string[];
  classification?: string[];
  issues?: string[];
  hierarchy?: string[];
  ruleset?: string[];
}

const RULESET_FILTERS = [{ value: 'changed', label: 'Changed by ruleset' }];

const NO_HIERARCHY_FILTER: string[] = [];

const HIERARCHY_FILTERS = [
  { value: 'Assembly', label: 'Assembly' },
  { value: 'Sub-Assembly', label: 'Sub-Assembly' },
  { value: 'Component', label: 'Component' },
];

interface Props {
  products: ImportProduct[];
  initialFilters?: ImportTableFilters;
  onFiltersChange?: (f: ImportTableFilters) => void;
  showDatabaseColumn?: boolean;
  showDatabaseFilter?: boolean;
  /** Enables team-shared saved views for one real product database. */
  savedViewDatabaseId?: string;
  databaseStructure?: 'hierarchy' | 'flat' | 'hts-code-grouping';
  /** Renders a link to a product detail. Defaults to plain <a href>. */
  renderProductLink?: RenderProductLink;
  /** Refetches the owning database after a persisted bulk operation. */
  onProductsChanged?: () => void;
  /** Opens the owning page's export dialog for the current filtered rows. */
  onExportProducts?: (productIds: string[]) => void;
  /** Optional owning-page action rendered beside the shared product search. */
  toolbarAction?: React.ReactNode;
  serverSide?: boolean;
  totalProducts?: number;
  page?: number;
  pageSize?: number;
  loading?: boolean;
  facets?: {
    origins: string[];
    databases: Array<{ id: string; name: string }>;
  };
  onPageChange?: (page: number) => void;
  onServerQueryChange?: (query: ImportTableServerQuery) => void;
}

export type SortKey =
  | 'name'
  | 'hts'
  | 'origin'
  | 'value'
  | 'baseDuty'
  | 'addDuty'
  | 'flags'
  | 'score';
export type SortDir = 'asc' | 'desc';

export interface ImportTableServerQuery {
  filters: ImportTableFilters;
  sortKey: SortKey;
  sortDir: SortDir;
}

type ColKey =
  | 'select'
  | 'item'
  | 'database'
  | 'hts'
  | 'origin'
  | 'value'
  | 'baseDuty'
  | 'addDuty'
  | 'flags'
  | 'score';

const ALL_COLS: { key: Exclude<ColKey, 'select' | 'item'>; label: string }[] = [
  { key: 'database', label: 'Database' },
  { key: 'hts', label: 'HTS Code' },
  { key: 'origin', label: 'Country of Origin' },
  { key: 'value', label: 'Product Value' },
  { key: 'baseDuty', label: 'Base Tariff' },
  { key: 'addDuty', label: 'Additional Tariff' },
  { key: 'flags', label: 'Flags' },
  { key: 'score', label: 'Compliance Score' },
];

const BULK_EDIT_FIELDS = [
  { value: 'country_of_origin', label: 'Country of Origin' },
  { value: 'category', label: 'Category' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'unit_of_measure', label: 'Unit of Measure' },
] as const;
type BulkEditFieldKey = (typeof BULK_EDIT_FIELDS)[number]['value'];
type BulkActionKey = 'delete' | 'edit' | 'exclusion' | 'fta';

/** Lightweight chapter→base-duty mock (USHTS-ish ranges). */
function baseDutyFor(hts: string): number {
  const ch = hts.slice(0, 2);
  const map: Record<string, number> = {
    '61': 16,
    '62': 16,
    '52': 8,
    '51': 7,
    '85': 0,
    '84': 2.5,
    '87': 2.5,
    '72': 0,
    '76': 3,
    '73': 3.7,
    '29': 0,
    '30': 0,
    '31': 0,
    '32': 6,
    '38': 3.7,
    '39': 4.2,
    '40': 4,
    '94': 3.2,
  };
  return map[ch] ?? 2.5;
}

const displayedPercent = (value: string | undefined): number | undefined => {
  if (value === undefined) return undefined;
  if (value.trim().toLowerCase() === 'free') return 0;
  const parsed = Number.parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const additionalDutyFor = (product: ImportProduct): number =>
  displayedPercent(product.additionalTariffDisplay) ??
  (product.section301 ? product.section301Rate : 0) +
    (product.section232 ? product.section232Rate : 0) +
    product.additionalDutyPct;

const baseDutyForProduct = (product: ImportProduct): number =>
  displayedPercent(product.baseDutyRate) ??
  (product.databaseProductId ? 0 : baseDutyFor(product.htsCode));

// Legacy ProductRow renders tariff_result.math.totalCost in its Product Value
// cell. Real Import Hub products carry that separately from product_value;
// mock products fall back to their existing declared value.
const productValueForTable = (product: ImportProduct): number =>
  product.totalCost ?? product.declaredValue;

const formatProductValue = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

interface ProductProgramFlag {
  key: string;
  label: string;
  title: string;
  tone: 'pga' | 'tariff' | 'adcvd' | 'fta' | 'labor';
}

// "United States–Mexico–Canada Agreement (USMCA)" → "USMCA"; names without a
// trailing abbreviation are shown as-is.
const ftaBadgeLabel = (program: string): string =>
  program.match(/\(([^()]+)\)\s*$/)?.[1].trim() || program;

/**
 * Trade/program flags shown in the database product table. These intentionally
 * do not use DatabaseProduct.issues: PGA exposure comes from screened PGA
 * records, while tariff/FTA applicability comes from the latest persisted
 * tariff result.
 */
const programFlagsFor = (product: ImportProduct): ProductProgramFlag[] => {
  const flags: ProductProgramFlag[] = product.pgaPrograms
    ? product.pgaPrograms.map((code) => {
        const program = pgaProgramInfo(code);
        return {
          key: `pga:${code}`,
          label: `PGA · ${program.short}`,
          title: `${program.name} PGA program flag`,
          tone: 'pga',
        };
      })
    : product.pgaFlags.map((agency) => ({
        key: `pga:${agency}`,
        label: `PGA · ${agency}`,
        title: `${agency} PGA program flag`,
        tone: 'pga',
      }));

  if (product.section301) {
    flags.push({
      key: 'tariff:301',
      label: '301',
      title: `Section 301${
        product.section301Rate > 0 ? ` · ${product.section301Rate}%` : ''
      }`,
      tone: 'tariff',
    });
  }
  if (product.section232) {
    flags.push({
      key: 'tariff:232',
      label: '232',
      title: `Section 232${
        product.section232Rate > 0 ? ` · ${product.section232Rate}%` : ''
      }`,
      tone: 'tariff',
    });
  }
  if (product.adcvdCases?.length) {
    flags.push({
      key: 'tariff:adcvd',
      label: 'AD/CVD',
      title: `Active AD/CVD order${
        product.adcvdCases.length === 1 ? '' : 's'
      } · ${product.adcvdCases.join(', ')}`,
      tone: 'adcvd',
    });
  }
  if (product.uflpaRisk !== 'none') {
    flags.push({
      key: 'program:uflpa',
      label: 'UFLPA',
      title: `UFLPA · ${product.uflpaRisk} risk`,
      tone: 'labor',
    });
  }
  for (const program of product.ftaPrograms ?? []) {
    flags.push({
      key: `fta:${program}`,
      label: ftaBadgeLabel(program),
      title: `${program} free trade agreement`,
      tone: 'fta',
    });
  }

  return flags;
};

interface InternalFilters {
  q: string;
  risk: string[];
  pga: string[];
  tariff: string[];
  valueBucket: string[];
  fta: string[];
  origin: string[];
  database: string[];
  addBucket: string[];
  scoreBucket: string[];
  classification: string[];
  issueType: string[];
  hierarchy: string[];
  ruleset: string[];
}

const emptyFilters = (init?: ImportTableFilters): InternalFilters => ({
  q: init?.q ?? '',
  risk: init?.risk ?? [],
  pga: init?.pga ?? [],
  tariff: init?.tariff ?? [],
  valueBucket: init?.value ?? [],
  fta: init?.fta ?? [],
  origin: init?.origin ?? [],
  database: init?.database ?? [],
  addBucket: init?.addDuty ?? [],
  scoreBucket: init?.score ?? [],
  classification: init?.classification ?? [],
  issueType: init?.issues ?? [],
  hierarchy: init?.hierarchy ?? [],
  ruleset: init?.ruleset ?? [],
});

const normalizeSavedFilters = (
  filters: Partial<InternalFilters> | undefined,
): InternalFilters => {
  const empty = emptyFilters();
  return {
    ...empty,
    q: typeof filters?.q === 'string' ? filters.q : '',
    risk: Array.isArray(filters?.risk) ? filters.risk : [],
    pga: Array.isArray(filters?.pga) ? filters.pga : [],
    tariff: Array.isArray(filters?.tariff) ? filters.tariff : [],
    valueBucket: Array.isArray(filters?.valueBucket) ? filters.valueBucket : [],
    fta: Array.isArray(filters?.fta) ? filters.fta : [],
    origin: Array.isArray(filters?.origin) ? filters.origin : [],
    database: Array.isArray(filters?.database) ? filters.database : [],
    addBucket: Array.isArray(filters?.addBucket) ? filters.addBucket : [],
    scoreBucket: Array.isArray(filters?.scoreBucket) ? filters.scoreBucket : [],
    classification: Array.isArray(filters?.classification)
      ? filters.classification
      : [],
    issueType: Array.isArray(filters?.issueType) ? filters.issueType : [],
    hierarchy: Array.isArray(filters?.hierarchy) ? filters.hierarchy : [],
    ruleset: Array.isArray(filters?.ruleset) ? filters.ruleset : [],
  };
};

const savedViewSummaryForFilters = (
  filters: InternalFilters,
): ImportSavedViewSummaryItem[] => {
  const summary: ImportSavedViewSummaryItem[] = [];
  const add = (label: string, values: string[]) => {
    if (values.length) summary.push({ label: label, values: values });
  };
  const labelsFor = (
    values: string[],
    options: Array<{ value: string; label: string }>,
  ) =>
    values.map(
      (value) =>
        options.find((option) => option.value === value)?.label ?? value,
    );

  if (filters.q.trim()) add('Search', [`“${filters.q.trim()}”`]);
  add('Hierarchy', filters.hierarchy);
  add('Risk', filters.risk);
  add('PGA', filters.pga);
  add(
    'Tariff',
    labelsFor(filters.tariff, [
      { value: '301', label: 'Section 301' },
      { value: '232', label: 'Section 232' },
      { value: 'adcvd', label: 'AD/CVD' },
      { value: 'additional', label: 'Any additional duty' },
    ]),
  );
  add('Value', labelsFor(filters.valueBucket, [...VALUE_BUCKETS]));
  add(
    'FTA',
    labelsFor(filters.fta, [
      { value: 'eligible', label: 'Eligible' },
      { value: 'ineligible', label: 'Ineligible' },
    ]),
  );
  add('Origin', filters.origin);
  add('Additional duty', labelsFor(filters.addBucket, ADD_BUCKETS));
  add('Compliance score', labelsFor(filters.scoreBucket, SCORE_BUCKETS));
  add('Classification', labelsFor(filters.classification, CLASS_HEALTH));
  add('Issues', labelsFor(filters.issueType, ISSUE_FILTERS));
  add('Ruleset', labelsFor(filters.ruleset, RULESET_FILTERS));
  return summary;
};

export const ImportProductsTable: React.FC<Props> = ({
  products,
  initialFilters,
  onFiltersChange,
  showDatabaseColumn,
  showDatabaseFilter,
  savedViewDatabaseId,
  databaseStructure = 'flat',
  renderProductLink = defaultRenderProductLink,
  onProductsChanged,
  onExportProducts,
  toolbarAction,
  serverSide = false,
  totalProducts,
  page = 1,
  pageSize = 25,
  loading = false,
  facets,
  onPageChange,
  onServerQueryChange,
}) => {
  const readOnly = useImportHubReadOnly();
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;
  const [f, setF] = useState<InternalFilters>(() =>
    emptyFilters(initialFilters),
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [relatedProducts, setRelatedProducts] = useState<
    Record<string, RelatedProductsState>
  >({});
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [hiddenCols, setHiddenCols] = useState<Set<ColKey>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectFilteredMode, setSelectFilteredMode] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<BulkActionKey | null>(null);
  const [bulkEditField, setBulkEditField] =
    useState<BulkEditFieldKey>('country_of_origin');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkExclusionOptions, setBulkExclusionOptions] = useState<
    ImportBulkTariffExclusionOption[]
  >([]);
  const [bulkExclusionCode, setBulkExclusionCode] = useState('');
  const [bulkExclusionMode, setBulkExclusionMode] = useState<
    'apply' | 'remove'
  >('apply');
  const [bulkExclusionLoading, setBulkExclusionLoading] = useState(false);
  const [bulkExclusionError, setBulkExclusionError] = useState<string | null>(
    null,
  );
  const [bulkExclusionScreenedCount, setBulkExclusionScreenedCount] =
    useState(0);
  const [bulkExclusionTargetIds, setBulkExclusionTargetIds] = useState<
    string[] | null
  >(null);
  const bulkExclusionRequestRef = useRef(0);
  const [bulkFtaOptions, setBulkFtaOptions] =
    useState<ImportBulkFtaOptionsResult | null>(null);
  const [bulkFtaMode, setBulkFtaMode] = useState<'apply' | 'remove'>('apply');
  const [bulkFtaLoading, setBulkFtaLoading] = useState(false);
  const [bulkFtaError, setBulkFtaError] = useState<string | null>(null);
  const [bulkFtaTargetIds, setBulkFtaTargetIds] = useState<string[] | null>(
    null,
  );
  const bulkFtaRequestRef = useRef(0);
  const [productCategories, setProductCategories] = useState<
    ImportProductCategory[]
  >([]);
  const [colsOpen, setColsOpen] = useState(false);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [savedViews, setSavedViews] = useState<ImportDatabaseSavedView[]>([]);
  const [savedViewsLoading, setSavedViewsLoading] = useState(
    Boolean(savedViewDatabaseId),
  );
  const [savedViewsError, setSavedViewsError] = useState<string | null>(null);
  const [savedViewsVersion, setSavedViewsVersion] = useState(0);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [savedViewMenuId, setSavedViewMenuId] = useState<string | null>(null);
  const [savedViewDialogMode, setSavedViewDialogMode] = useState<
    'create' | 'rename' | null
  >(null);
  const [savedViewDialogTarget, setSavedViewDialogTarget] =
    useState<ImportDatabaseSavedView | null>(null);
  const [savedViewSaving, setSavedViewSaving] = useState(false);
  const [savedViewDialogError, setSavedViewDialogError] = useState<
    string | null
  >(null);
  const [savedViewDeleteTarget, setSavedViewDeleteTarget] =
    useState<ImportDatabaseSavedView | null>(null);
  const [savedViewDeleting, setSavedViewDeleting] = useState(false);

  const initialFiltersKey = JSON.stringify(initialFilters ?? {});

  // Priority Issue links and browser navigation can change URL-backed filters
  // after the table has mounted. Keep the controlled view synchronized.
  useEffect(() => {
    setF(emptyFilters(initialFilters));
    setSelected(new Set());
    setSelectFilteredMode(false);
  }, [initialFiltersKey]);

  useEffect(() => {
    setActiveViewId(null);
    setSavedViewMenuId(null);
    setHiddenCols(new Set());
  }, [savedViewDatabaseId]);

  const relatedCacheKey = products
    .map((product) => `${product.id}:${product.childProductCount ?? 0}`)
    .join('|');
  useEffect(() => {
    setRelatedProducts({});
    setExpanded(new Set());
  }, [relatedCacheKey]);

  useEffect(() => {
    if (!serverSide) return;
    setSelected(new Set());
    setSelectFilteredMode(false);
  }, [page, serverSide]);

  /* Shared database-scoped saved views */
  useEffect(() => {
    if (!savedViewDatabaseId) {
      setSavedViews([]);
      setSavedViewsError(null);
      setSavedViewsLoading(false);
      return;
    }
    let cancelled = false;
    setSavedViewsLoading(true);
    setSavedViewsError(null);
    const loadSavedViews = async () => {
      try {
        const { success, views, error } = await fetchImportDatabaseSavedViews(
          savedViewDatabaseId,
        );
        if (cancelled) return;
        if (success) {
          setSavedViews(views);
        } else {
          setSavedViews([]);
          setSavedViewsError(error ?? 'Failed to load saved views');
        }
      } catch {
        if (cancelled) return;
        setSavedViews([]);
        setSavedViewsError('Failed to load saved views');
      } finally {
        if (!cancelled) setSavedViewsLoading(false);
      }
    };
    void loadSavedViews();
    return () => {
      cancelled = true;
    };
  }, [savedViewDatabaseId, savedViewsVersion]);

  /* Notify parent so every applied saved-view filter is URL-synchronized. */
  useEffect(() => {
    onFiltersChange?.({
      q: f.q,
      risk: f.risk,
      pga: f.pga,
      tariff: f.tariff,
      value: f.valueBucket,
      fta: f.fta,
      origin: f.origin,
      database: f.database,
      addDuty: f.addBucket,
      score: f.scoreBucket,
      classification: f.classification,
      issues: f.issueType,
      hierarchy: f.hierarchy,
      ruleset: f.ruleset,
    });
  }, [
    f.q,
    f.risk,
    f.pga,
    f.tariff,
    f.valueBucket,
    f.fta,
    f.origin,
    f.database,
    f.addBucket,
    f.scoreBucket,
    f.classification,
    f.issueType,
    f.hierarchy,
    f.ruleset,
  ]);

  const hierarchyFilter =
    databaseStructure === 'hierarchy' ? f.hierarchy : NO_HIERARCHY_FILTER;

  useEffect(() => {
    if (!serverSide || !onServerQueryChange) return;
    const rawSearch = f.q.trim();
    const normalizedHtsSearch = /^[0-9.\s-]+$/.test(rawSearch)
      ? normalizeHtsForSearch(rawSearch)
      : null;
    const timer = window.setTimeout(
      () => {
        onServerQueryChange({
          filters: {
            q: normalizedHtsSearch ?? f.q,
            risk: f.risk,
            pga: f.pga,
            tariff: f.tariff,
            value: f.valueBucket,
            fta: f.fta,
            origin: f.origin,
            database: f.database,
            addDuty: f.addBucket,
            score: f.scoreBucket,
            classification: f.classification,
            issues: f.issueType,
            hierarchy: hierarchyFilter,
            ruleset: f.ruleset,
          },
          sortKey,
          sortDir,
        });
      },
      f.q ? 250 : 0,
    );
    return () => window.clearTimeout(timer);
  }, [serverSide, onServerQueryChange, f, hierarchyFilter, sortKey, sortDir]);

  const clearSelectionForFilterChange = () => {
    setSelected(new Set());
    setSelectFilteredMode(false);
  };

  const update = <K extends keyof InternalFilters>(
    k: K,
    v: InternalFilters[K],
  ) => {
    setF((prev) => ({ ...prev, [k]: v }));
    setActiveViewId(null);
    clearSelectionForFilterChange();
  };
  const toggleIn = (k: keyof InternalFilters, v: string) => {
    const arr = (f[k] as string[]) ?? [];
    update(
      k,
      (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]) as never,
    );
  };

  const origins = useMemo(
    () =>
      facets?.origins ??
      Array.from(
        new Set(products.map((p) => p.countryOfOrigin).filter(Boolean)),
      ).sort(),
    [facets, products],
  );
  const databases = useMemo(() => {
    if (facets?.databases?.length) return facets.databases;
    const byId = new Map<string, string>();
    products.forEach((product) => {
      const mockName = IMPORT_DATABASES.find(
        ({ id }) => id === product.importDatabaseId,
      )?.name;
      byId.set(
        product.importDatabaseId,
        product.databaseName ?? mockName ?? product.importDatabaseId,
      );
    });
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [facets, products]);

  const filtered = useMemo(() => {
    if (serverSide) return products;
    const ql = f.q.trim().toLowerCase();
    const normalizedHtsQuery = ql.replace(/[^0-9]/g, '');
    const isHtsQuery =
      normalizedHtsQuery.length >= 4 && /^[0-9.\s-]+$/.test(ql);
    return products.filter((p) => {
      if (ql) {
        const hay = `${p.name} ${p.sku} ${p.htsCode} ${p.countryOfOrigin} ${
          p.description
        } ${p.manufacturer ?? ''} ${p.vendor ?? ''}`.toLowerCase();
        const normalizedHtsCode = p.htsCode.replace(/[^0-9]/g, '');
        if (
          !hay.includes(ql) &&
          !(isHtsQuery && normalizedHtsCode.includes(normalizedHtsQuery))
        )
          return false;
      }
      if (f.risk.length && !f.risk.includes(p.riskLevel)) return false;
      if (
        f.pga.length &&
        !f.pga.some(
          (a) =>
            p.pgaPrograms?.includes(a) || p.pgaFlags.includes(a as PgaAgency),
        )
      )
        return false;
      if (f.tariff.length) {
        const ok = f.tariff.some(
          (t) =>
            (t === '301' && p.section301) ||
            (t === '232' && p.section232) ||
            (t === 'adcvd' && (p.adcvdCases?.length ?? 0) > 0) ||
            (t === 'additional' && additionalDutyFor(p) > 0),
        );
        if (!ok) return false;
      }
      if (f.valueBucket.length) {
        const value = productValueForTable(p);
        const ok = f.valueBucket.some((bucket) =>
          bucket === '0'
            ? value === 0
            : bucket === '0-1k'
            ? value > 0 && value <= 1000
            : bucket === '1k-10k'
            ? value > 1000 && value <= 10000
            : bucket === '10k-100k'
            ? value > 10000 && value <= 100000
            : value > 100000,
        );
        if (!ok) return false;
      }
      if (f.fta.length) {
        if (p.ftaEvaluated === false) return false;
        const want = f.fta.includes('eligible');
        const wantNot = f.fta.includes('ineligible');
        if (want && !wantNot && !p.ftaEligible) return false;
        if (wantNot && !want && p.ftaEligible) return false;
      }
      if (f.origin.length && !f.origin.includes(p.countryOfOrigin))
        return false;
      if (f.ruleset.includes('changed') && !p.changedByRuleset) return false;
      if (f.database.length && !f.database.includes(p.importDatabaseId))
        return false;
      if (f.addBucket.length) {
        const d = additionalDutyFor(p);
        const ok = f.addBucket.some((b) =>
          b === '0'
            ? d === 0
            : b === '0-10'
            ? d > 0 && d <= 10
            : b === '10-25'
            ? d > 10 && d <= 25
            : d > 25,
        );
        if (!ok) return false;
      }
      if (f.scoreBucket.length) {
        if (p.complianceAssessed === false) return false;
        const s = p.complianceScore;
        const ok = f.scoreBucket.some((b) =>
          b === 'low' ? s < 65 : b === 'mid' ? s >= 65 && s < 85 : s >= 85,
        );
        if (!ok) return false;
      }
      if (f.classification.length) {
        const ok = f.classification.some((c) =>
          c === 'missing-hts'
            ? !p.htsCode
            : c === 'invalid-hts'
            ? isHtsInvalid(p)
            : c === 'missing-coo'
            ? !p.countryOfOrigin
            : false,
        );
        if (!ok) return false;
      }
      if (
        f.issueType.length &&
        !f.issueType.some((issue) =>
          p.issueTypes?.includes(issue as ImportIssueType),
        )
      ) {
        return false;
      }
      if (
        hierarchyFilter.length &&
        !hierarchyFilter.includes(p.hierarchyType ?? '')
      ) {
        return false;
      }
      return true;
    });
  }, [products, f, hierarchyFilter, serverSide]);

  const sorted = useMemo(() => {
    if (serverSide) return filtered;
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'hts':
          return a.htsCode.localeCompare(b.htsCode) * dir;
        case 'origin':
          return a.countryOfOrigin.localeCompare(b.countryOfOrigin) * dir;
        case 'value':
          return (productValueForTable(a) - productValueForTable(b)) * dir;
        case 'baseDuty':
          return (baseDutyForProduct(a) - baseDutyForProduct(b)) * dir;
        case 'addDuty':
          return (additionalDutyFor(a) - additionalDutyFor(b)) * dir;
        case 'flags':
          return (
            (programFlagsFor(a).length - programFlagsFor(b).length) * dir ||
            a.name.localeCompare(b.name) * dir
          );
        case 'score':
          if (a.complianceAssessed === false) {
            return b.complianceAssessed === false ? 0 : 1;
          }
          if (b.complianceAssessed === false) return -1;
          return (a.complianceScore - b.complianceScore) * dir;
      }
    });
    return arr;
  }, [filtered, serverSide, sortKey, sortDir]);

  const clearAll = () => {
    setF(emptyFilters());
    setActiveViewId(null);
    setSelected(new Set());
    setSelectFilteredMode(false);
  };

  const activeCount =
    (f.q ? 1 : 0) +
    f.risk.length +
    f.pga.length +
    f.tariff.length +
    f.valueBucket.length +
    f.fta.length +
    f.origin.length +
    f.database.length +
    f.addBucket.length +
    f.scoreBucket.length +
    f.classification.length +
    f.issueType.length +
    f.ruleset.length +
    hierarchyFilter.length;

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      setSortDir(
        k === 'name' || k === 'hts' || k === 'origin' ? 'asc' : 'desc',
      );
    }
  };

  const loadRelated = (product: ImportProduct) => {
    const cached = relatedProducts[product.id];
    if (cached && !cached.error) return;
    const setFailed = (error?: string) =>
      setRelatedProducts((current) => ({
        ...current,
        [product.id]: {
          loading: false,
          products: [],
          error: error ?? 'Failed to load related products',
        },
      }));
    const databaseId = product.importDatabaseId || savedViewDatabaseId;
    if (!databaseId) {
      setFailed();
      return;
    }
    setRelatedProducts((current) => ({
      ...current,
      [product.id]: { loading: true, products: [] },
    }));
    fetchImportProductChildren(product.id, databaseId)
      .then(({ success, children, error }) => {
        if (!success) {
          setFailed(error);
          return;
        }
        setRelatedProducts((current) => ({
          ...current,
          [product.id]: {
            loading: false,
            products: children.map((child) =>
              toImportProduct(child, databaseId),
            ),
          },
        }));
      })
      .catch(() => setFailed());
  };

  const toggleExpand = (product: ImportProduct) => {
    if (!product.childProductCount) return;
    const expanding = !expanded.has(product.id);
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(product.id)) next.delete(product.id);
      else next.add(product.id);
      return next;
    });
    if (expanding) loadRelated(product);
  };

  /* Selection */
  const visibleIds = sorted.map((p) => p.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected = visibleIds.some((id) => selected.has(id));
  const toggleSelectAllVisible = () => {
    const next = new Set(selected);
    if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    setSelected(next);
    setSelectFilteredMode(false);
  };
  const toggleSelected = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    setSelectFilteredMode(false);
  };
  const effectiveSelected =
    selectFilteredMode && !serverSide ? new Set(visibleIds) : selected;
  const effectiveCount =
    selectFilteredMode && !serverSide ? sorted.length : selected.size;

  const visibleCol = (k: ColKey) => !hiddenCols.has(k);
  const toggleCol = (k: ColKey) => {
    const next = new Set(hiddenCols);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setHiddenCols(next);
    setActiveViewId(null);
  };

  /* Saved views */
  const currentSavedViewConfiguration =
    (): ImportDatabaseSavedViewConfiguration => ({
      version: 1,
      filters: {
        q: f.q,
        risk: f.risk,
        pga: f.pga,
        tariff: f.tariff,
        valueBucket: f.valueBucket,
        fta: f.fta,
        origin: f.origin,
        addBucket: f.addBucket,
        scoreBucket: f.scoreBucket,
        classification: f.classification,
        issueType: f.issueType,
        hierarchy: f.hierarchy,
        ruleset: f.ruleset,
      },
      hiddenColumns: ALL_COLS.map((column) => column.key).filter(
        (column): column is ImportSavedViewColumn =>
          hiddenCols.has(column as ColKey),
      ),
    });
  const sortSavedViews = (views: ImportDatabaseSavedView[]) =>
    [...views].sort((left, right) => left.name.localeCompare(right.name));
  const applyView = (view: ImportDatabaseSavedView) => {
    setF(normalizeSavedFilters(view.configuration.filters));
    setHiddenCols(
      new Set(
        view.configuration.hiddenColumns.map((column) => column as ColKey),
      ),
    );
    setActiveViewId(view.id);
    setSavedViewMenuId(null);
    clearSelectionForFilterChange();
  };
  const openCreateSavedView = () => {
    setSavedViewDialogTarget(null);
    setSavedViewDialogError(null);
    setSavedViewDialogMode('create');
  };
  const openRenameSavedView = (view: ImportDatabaseSavedView) => {
    setSavedViewMenuId(null);
    setSavedViewDialogTarget(view);
    setSavedViewDialogError(null);
    setSavedViewDialogMode('rename');
  };
  const closeSavedViewDialog = () => {
    if (savedViewSaving) return;
    setSavedViewDialogMode(null);
    setSavedViewDialogTarget(null);
    setSavedViewDialogError(null);
  };
  const saveSavedViewDialog = async (name: string) => {
    if (!savedViewDatabaseId || !savedViewDialogMode) return;
    setSavedViewSaving(true);
    setSavedViewDialogError(null);
    if (savedViewDialogMode === 'create') {
      const result = await createImportDatabaseSavedView(
        savedViewDatabaseId,
        name,
        currentSavedViewConfiguration(),
      );
      setSavedViewSaving(false);
      if (!result.success || !result.view) {
        setSavedViewDialogError(result.error ?? 'Failed to save view');
        return;
      }
      setSavedViews((current) =>
        sortSavedViews([...current, result.view as ImportDatabaseSavedView]),
      );
      setActiveViewId(result.view.id);
      closeSavedViewDialog();
      toast.success('Saved view created.');
      return;
    }

    if (!savedViewDialogTarget) {
      setSavedViewSaving(false);
      return;
    }
    const result = await updateImportDatabaseSavedView(
      savedViewDatabaseId,
      savedViewDialogTarget.id,
      { name: name },
    );
    setSavedViewSaving(false);
    if (!result.success || !result.view) {
      setSavedViewDialogError(result.error ?? 'Failed to rename saved view');
      return;
    }
    setSavedViews((current) =>
      sortSavedViews(
        current.map((view) =>
          view.id === result.view?.id
            ? (result.view as ImportDatabaseSavedView)
            : view,
        ),
      ),
    );
    closeSavedViewDialog();
    toast.success('Saved view renamed.');
  };
  const overwriteSavedView = async (view: ImportDatabaseSavedView) => {
    if (!savedViewDatabaseId || !view.canManage) return;
    setSavedViewMenuId(null);
    const result = await updateImportDatabaseSavedView(
      savedViewDatabaseId,
      view.id,
      { configuration: currentSavedViewConfiguration() },
    );
    if (!result.success || !result.view) {
      toast.error(result.error ?? 'Failed to update saved view');
      return;
    }
    setSavedViews((current) =>
      current.map((savedView) =>
        savedView.id === result.view?.id
          ? (result.view as ImportDatabaseSavedView)
          : savedView,
      ),
    );
    setActiveViewId(result.view.id);
    toast.success('Saved view updated with the current filters.');
  };
  const deleteSavedView = async () => {
    if (
      !savedViewDatabaseId ||
      !savedViewDeleteTarget ||
      !savedViewDeleteTarget.canManage
    )
      return;
    setSavedViewDeleting(true);
    const result = await deleteImportDatabaseSavedView(
      savedViewDatabaseId,
      savedViewDeleteTarget.id,
    );
    setSavedViewDeleting(false);
    if (!result.success) {
      toast.error(result.error ?? 'Failed to delete saved view');
      return;
    }
    setSavedViews((current) =>
      current.filter((view) => view.id !== savedViewDeleteTarget.id),
    );
    if (activeViewId === savedViewDeleteTarget.id) setActiveViewId(null);
    setSavedViewDeleteTarget(null);
    toast.success('Saved view deleted.');
  };

  /* Persisted bulk actions. "Edit field" has no batch endpoint — it fans out
     client-side over the same per-product PATCH the detail page uses. */
  const selectedRowsForBulkAction = () =>
    sorted.filter((product) => effectiveSelected.has(product.id));
  const databaseProductIdsForBulkAction = (): string[] | null => {
    const selectedRows = selectedRowsForBulkAction();
    const databaseProductIds = selectedRows
      .map((product) => product.databaseProductId)
      .filter((value): value is string => Boolean(value));
    return databaseProductIds.length === selectedRows.length
      ? databaseProductIds
      : null;
  };

  const openBulkAction = (action: BulkActionKey) => {
    setBulkOpen(false);
    if (!savedViewDatabaseId || effectiveCount === 0) return;
    if (action === 'edit') {
      setBulkEditField('country_of_origin');
      setBulkEditValue('');
      setBulkProgress(null);
    }
    setBulkAction(action);
    if (action === 'fta') {
      const ftaTargetIds = databaseProductIdsForBulkAction();
      setBulkFtaOptions(null);
      setBulkFtaMode('apply');
      setBulkFtaTargetIds(ftaTargetIds);
      setBulkFtaError(null);
      if (!ftaTargetIds) {
        setBulkFtaError(
          'One or more selected products are missing their database association.',
        );
        return;
      }

      const ftaRequestId = ++bulkFtaRequestRef.current;
      setBulkFtaLoading(true);
      void (async () => {
        try {
          const result = await fetchBulkImportFtaOptions(
            savedViewDatabaseId,
            ftaTargetIds,
          );
          if (ftaRequestId !== bulkFtaRequestRef.current) return;
          if (!result.success) {
            setBulkFtaError(result.error ?? 'Failed to load applicable FTAs');
            return;
          }
          setBulkFtaOptions(result);
        } catch (error) {
          if (ftaRequestId !== bulkFtaRequestRef.current) return;
          setBulkFtaError(
            error instanceof Error
              ? error.message
              : 'Failed to load applicable FTAs',
          );
        } finally {
          if (ftaRequestId === bulkFtaRequestRef.current) {
            setBulkFtaLoading(false);
          }
        }
      })();
      return;
    }
    if (action !== 'exclusion') return;

    const databaseProductIds = databaseProductIdsForBulkAction();
    setBulkExclusionOptions([]);
    setBulkExclusionCode('');
    setBulkExclusionMode('apply');
    setBulkExclusionScreenedCount(0);
    setBulkExclusionTargetIds(databaseProductIds);
    setBulkExclusionError(null);
    if (!databaseProductIds) {
      setBulkExclusionError(
        'One or more selected products are missing their database association.',
      );
      return;
    }

    const requestId = ++bulkExclusionRequestRef.current;
    setBulkExclusionLoading(true);
    void (async () => {
      try {
        const result = await fetchBulkImportTariffExclusionOptions(
          savedViewDatabaseId,
          databaseProductIds,
        );
        if (requestId !== bulkExclusionRequestRef.current) return;
        if (!result.success) {
          setBulkExclusionError(
            result.error ?? 'Failed to load applicable tariff exclusions',
          );
          return;
        }
        setBulkExclusionOptions(result.options);
        setBulkExclusionScreenedCount(result.screenedCount);
        const preferred =
          result.options.find((option) => option.applyUpdateCount > 0) ??
          result.options[0];
        setBulkExclusionCode(preferred?.code ?? '');
      } catch (error) {
        if (requestId !== bulkExclusionRequestRef.current) return;
        setBulkExclusionError(
          error instanceof Error
            ? error.message
            : 'Failed to load applicable tariff exclusions',
        );
      } finally {
        if (requestId === bulkExclusionRequestRef.current) {
          setBulkExclusionLoading(false);
        }
      }
    })();
  };
  const closeBulkAction = () => {
    if (bulkSaving) return;
    bulkExclusionRequestRef.current += 1;
    bulkFtaRequestRef.current += 1;
    setBulkAction(null);
    setBulkEditValue('');
    setBulkProgress(null);
    setBulkExclusionOptions([]);
    setBulkExclusionCode('');
    setBulkExclusionTargetIds(null);
    setBulkExclusionError(null);
    setBulkExclusionLoading(false);
    setBulkFtaOptions(null);
    setBulkFtaTargetIds(null);
    setBulkFtaError(null);
    setBulkFtaLoading(false);
  };

  // Lazy-loaded the first time the edit modal opens, so switching the field
  // picker to "Category" has options ready without its own loading state.
  useEffect(() => {
    if (bulkAction !== 'edit' || !groupId || productCategories.length > 0) {
      return;
    }
    let cancelled = false;
    fetchImportProductCategories(groupId).then(
      ({ success, categories, error }) => {
        if (cancelled) return;
        if (success) {
          setProductCategories(categories);
        } else {
          toast.error(error ?? 'Failed to load product categories');
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [bulkAction, groupId, productCategories.length]);

  const buildBulkEditUpdate = (): ImportProductDetailsUpdate => {
    const trimmed = bulkEditValue.trim();
    switch (bulkEditField) {
      case 'country_of_origin':
        return { country_of_origin: trimmed };
      case 'category':
        return { category: trimmed || null };
      case 'manufacturer':
        return { manufacturer: trimmed || null };
      case 'vendor':
        return { vendor: trimmed || null };
      case 'unit_of_measure':
        return { unit_of_measure: trimmed || null };
      default:
        return {};
    }
  };

  const confirmBulkAction = async () => {
    if (!bulkAction || !savedViewDatabaseId || bulkSaving) return;
    if (
      bulkAction === 'edit' &&
      bulkEditField === 'country_of_origin' &&
      !bulkEditValue
    ) {
      return;
    }
    if (bulkAction === 'exclusion' && !bulkExclusionCode) return;
    if (bulkAction === 'fta' && bulkFtaUpdateCount === 0) return;
    const databaseProductIds =
      bulkAction === 'exclusion'
        ? bulkExclusionTargetIds
        : bulkAction === 'fta'
        ? bulkFtaTargetIds
        : databaseProductIdsForBulkAction();
    if (!databaseProductIds) {
      toast.error(
        'One or more selected products are missing their database association.',
      );
      return;
    }

    setBulkSaving(true);
    try {
      if (bulkAction === 'delete') {
        const result = await bulkDeleteImportDatabaseProducts(
          savedViewDatabaseId,
          databaseProductIds,
        );
        if (!result.success) {
          toast.error(result.error ?? 'Failed to remove selected products');
          return;
        }
        toast.success(
          `Removed ${result.deleted} product${
            result.deleted === 1 ? '' : 's'
          } from this database.`,
        );
      } else if (bulkAction === 'exclusion') {
        const result = await bulkUpdateImportTariffExclusion(
          savedViewDatabaseId,
          databaseProductIds,
          bulkExclusionCode,
          bulkExclusionMode,
        );
        if (!result.success) {
          toast.error(result.error ?? 'Failed to update tariff exclusions');
          return;
        }
        const actionLabel =
          bulkExclusionMode === 'apply' ? 'Applied' : 'Removed';
        if (result.failed > 0) {
          toast.warning(
            `${actionLabel} the exclusion for ${result.updated} product${
              result.updated === 1 ? '' : 's'
            }; ${result.failed} failed.`,
          );
        } else if (result.updated === 0) {
          toast.info(
            `No products required an update. ${
              result.inapplicable > 0
                ? `${result.inapplicable} were not applicable.`
                : 'The selected products were already up to date.'
            }`,
          );
        } else {
          const skipped = result.unchanged + result.inapplicable;
          toast.success(
            `${actionLabel} the exclusion for ${result.updated} product${
              result.updated === 1 ? '' : 's'
            }${skipped > 0 ? `; ${skipped} did not require a change` : ''}.`,
          );
        }
      } else if (bulkAction === 'fta') {
        const result = await bulkUpdateImportFtas(
          savedViewDatabaseId,
          databaseProductIds,
          bulkFtaMode,
        );
        if (!result.success) {
          toast.error(result.error ?? 'Failed to update FTAs');
          return;
        }
        const actionLabel =
          bulkFtaMode === 'apply'
            ? 'Turned on applicable FTAs'
            : 'Turned off FTAs';
        const splitNote =
          result.usContentSplit > 0
            ? ` ${result.usContentSplit} product${
                result.usContentSplit === 1 ? ' now includes' : 's now include'
              } a U.S.-content split priced at the product's material percentage (40% by default).`
            : '';
        if (result.failed > 0) {
          toast.warning(
            `${actionLabel} for ${result.updated} product${
              result.updated === 1 ? '' : 's'
            }; ${result.failed} failed.${splitNote}`,
          );
        } else if (result.updated === 0) {
          toast.info(
            `No products required an update. ${
              result.inapplicable > 0
                ? `${result.inapplicable} had no applicable FTAs.`
                : 'The selected products were already up to date.'
            }`,
          );
        } else {
          const skipped = result.unchanged + result.inapplicable;
          toast.success(
            `${actionLabel} for ${result.updated} product${
              result.updated === 1 ? '' : 's'
            }${
              skipped > 0 ? `; ${skipped} did not require a change` : ''
            }.${splitNote}`,
          );
        }
      } else {
        setBulkProgress({ done: 0, total: databaseProductIds.length });
        const result = await bulkUpdateImportProductDetails(
          databaseProductIds,
          buildBulkEditUpdate(),
          (done, total) => setBulkProgress({ done, total }),
        );
        if (result.failed === 0) {
          toast.success(
            `Updated ${result.updated} product${
              result.updated === 1 ? '' : 's'
            }.`,
          );
        } else if (result.updated === 0) {
          toast.error('Failed to update the selected products.');
        } else {
          toast.warning(
            `Updated ${result.updated} of ${
              result.updated + result.failed
            } products. ${result.failed} failed.`,
          );
        }
      }
      setBulkAction(null);
      setBulkEditValue('');
      setBulkProgress(null);
      setBulkExclusionOptions([]);
      setBulkExclusionCode('');
      setBulkExclusionTargetIds(null);
      setBulkExclusionError(null);
      setBulkFtaOptions(null);
      setBulkFtaTargetIds(null);
      setBulkFtaError(null);
      setSelected(new Set());
      setSelectFilteredMode(false);
      onProductsChanged?.();
    } catch {
      toast.error(
        bulkAction === 'delete'
          ? 'Failed to remove selected products'
          : bulkAction === 'exclusion'
          ? 'Failed to update tariff exclusions'
          : bulkAction === 'fta'
          ? 'Failed to update FTAs'
          : 'Failed to update the selected products.',
      );
    } finally {
      setBulkSaving(false);
    }
  };

  /* Chip removers */
  const removeChip = (group: keyof InternalFilters, v: string) =>
    update(group, (f[group] as string[]).filter((x) => x !== v) as never);

  const totalCols =
    1 + // chevron
    1 + // select
    1 + // item
    ALL_COLS.filter(
      (c) =>
        visibleCol(c.key as ColKey) &&
        (showDatabaseColumn || c.key !== 'database'),
    ).length;
  const savedViewSummary = savedViewSummaryForFilters(f);
  const visibleSavedColumnCount = ALL_COLS.filter(
    (column) =>
      !hiddenCols.has(column.key as ColKey) &&
      (showDatabaseColumn || column.key !== 'database'),
  ).length;
  const moreFilterCount =
    f.addBucket.length +
    f.scoreBucket.length +
    f.valueBucket.length +
    f.fta.length +
    f.origin.length +
    f.classification.length +
    f.issueType.length +
    f.ruleset.length +
    f.database.length;
  const selectedBulkExclusionOption = bulkExclusionOptions.find(
    (option) => option.code === bulkExclusionCode,
  );
  const bulkExclusionUpdateCount = selectedBulkExclusionOption
    ? bulkExclusionMode === 'apply'
      ? selectedBulkExclusionOption.applyUpdateCount
      : selectedBulkExclusionOption.appliedCount
    : 0;
  const bulkExclusionTargetCount = bulkExclusionTargetIds?.length ?? 0;
  const bulkFtaUpdateCount = bulkFtaOptions
    ? bulkFtaMode === 'apply'
      ? bulkFtaOptions.applyUpdateCount
      : bulkFtaOptions.removeUpdateCount
    : 0;
  const bulkFtaTargetCount = bulkFtaTargetIds?.length ?? 0;

  return (
    <div className='ih-products-list'>
      <div className='ih-products-toolbar'>
        <div className='ih-products-search'>
          <Search size={18} />
          <Input
            value={f.q}
            onChange={(event) => update('q', event.target.value)}
            placeholder='Search by name, SKU, or HTS'
            aria-label='Search products'
            className='ih-products-search-input'
          />
        </div>
        {toolbarAction && (
          <div className='ih-products-toolbar-actions'>{toolbarAction}</div>
        )}
      </div>

      {/* Saved views chips row */}
      {savedViewDatabaseId && (
        <div className='ih-saved-views-row'>
          <span className='ih-saved-views-label'>Saved views</span>
          {savedViewsLoading && (
            <span className='ih-saved-views-state'>
              <span className='ih-spinner ih-saved-views-spinner' />
              Loading…
            </span>
          )}
          {!savedViewsLoading && savedViewsError && (
            <span className='ih-saved-views-state is-error'>
              {savedViewsError}
              <button
                type='button'
                onClick={() => setSavedViewsVersion((version) => version + 1)}
              >
                Retry
              </button>
            </span>
          )}
          {!savedViewsLoading &&
            !savedViewsError &&
            savedViews.length === 0 && (
              <span className='ih-saved-views-state'>None yet</span>
            )}
          {!savedViewsLoading &&
            !savedViewsError &&
            savedViews.map((view) => (
              <span
                key={view.id}
                className={cn(
                  'ih-saved-view-chip',
                  activeViewId === view.id && 'is-active',
                )}
                title={`Created by ${view.createdByName ?? 'a team member'}`}
              >
                <button
                  type='button'
                  className='ih-saved-view-apply'
                  onClick={() => applyView(view)}
                >
                  {view.name}
                </button>
                {view.canManage && (
                  <span className='ih-saved-view-actions'>
                    <button
                      type='button'
                      className='ih-saved-view-menu-trigger'
                      aria-label={`Manage ${view.name}`}
                      disabled={readOnly}
                      title={
                        readOnly ? 'Requires a Business license' : undefined
                      }
                      onClick={() =>
                        setSavedViewMenuId((current) =>
                          current === view.id ? null : view.id,
                        )
                      }
                    >
                      <span aria-hidden='true'>⋮</span>
                    </button>
                    {savedViewMenuId === view.id && (
                      <span className='ih-saved-view-menu'>
                        <button
                          type='button'
                          onClick={() => openRenameSavedView(view)}
                        >
                          Rename
                        </button>
                        <button
                          type='button'
                          onClick={() => overwriteSavedView(view)}
                        >
                          Update with current filters
                        </button>
                        <button
                          type='button'
                          className='is-danger'
                          onClick={() => {
                            setSavedViewMenuId(null);
                            setSavedViewDeleteTarget(view);
                          }}
                        >
                          Delete
                        </button>
                      </span>
                    )}
                  </span>
                )}
              </span>
            ))}
          <button
            type='button'
            onClick={openCreateSavedView}
            className='ih-save-view-button'
            disabled={
              (activeCount === 0 && hiddenCols.size === 0) ||
              savedViewsLoading ||
              readOnly
            }
            title={
              readOnly
                ? 'Requires a Business license'
                : activeCount === 0 && hiddenCols.size === 0
                ? 'Select a filter or hide a column before saving a view'
                : 'Save current filters and column visibility'
            }
          >
            {readOnly ? (
              <Lock className='size-3.5' />
            ) : (
              <Bookmark className='size-3.5' />
            )}{' '}
            Save current view
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className='ih-products-filterbar'>
        <div className='ih-products-facet-row'>
          {databaseStructure === 'hierarchy' && (
            <MultiFacet
              label='Hierarchy'
              options={HIERARCHY_FILTERS}
              selected={f.hierarchy}
              onChange={(next) => update('hierarchy', next)}
            />
          )}
          <MultiFacet
            label='Risk'
            options={RISKS.map((risk) => ({ value: risk, label: risk }))}
            selected={f.risk}
            onChange={(next) => update('risk', next)}
          />
          <MultiFacet
            label='PGA'
            options={
              serverSide
                ? PGA_PROGRAMS.map((program) => ({
                    value: program.code,
                    label: program.name,
                  }))
                : PGA_AGENCIES.map((agency) => ({
                    value: agency,
                    label: agency,
                  }))
            }
            selected={f.pga}
            onChange={(next) => update('pga', next)}
          />
          <MultiFacet
            label='Tariff'
            options={[
              { value: '301', label: 'Section 301' },
              { value: '232', label: 'Section 232' },
              { value: 'adcvd', label: 'AD/CVD' },
              { value: 'additional', label: 'Additional duty' },
            ]}
            selected={f.tariff}
            onChange={(next) => update('tariff', next)}
          />
          <button
            type='button'
            className={cn(
              'ih-products-facet-button',
              moreFilterCount > 0 && 'is-active',
            )}
            onClick={() => setMoreFiltersOpen((current) => !current)}
            aria-expanded={moreFiltersOpen}
          >
            <Settings2 size={16} /> More filters
            {moreFilterCount > 0 && (
              <span className='ih-products-facet-count'>{moreFilterCount}</span>
            )}
          </button>

          <div className='ih-products-facet-spacer' />

          <Dropdown
            open={colsOpen}
            onOpenChange={setColsOpen}
            align='right'
            trigger={
              <button className='ih-products-facet-button'>
                <Columns3 size={16} /> Columns
                <span className='ih-products-facet-count'>
                  {visibleSavedColumnCount}
                </span>
                <ChevronDown size={14} />
              </button>
            }
          >
            <div className='ih-products-facet-menu-content'>
              {ALL_COLS.map((column) => {
                if (column.key === 'database' && !showDatabaseColumn)
                  return null;
                const checked = visibleCol(column.key as ColKey);
                return (
                  <button
                    key={column.key}
                    type='button'
                    onClick={(event) => {
                      event.preventDefault();
                      toggleCol(column.key as ColKey);
                    }}
                    className='ih-products-facet-option'
                  >
                    <span
                      className={cn(
                        'ih-products-facet-check',
                        checked && 'is-checked',
                      )}
                    >
                      {checked && '✓'}
                    </span>
                    <span>{column.label}</span>
                  </button>
                );
              })}
            </div>
          </Dropdown>
        </div>

        {moreFiltersOpen && (
          <div className='ih-products-facet-row ih-products-facet-row-more'>
            <MultiFacet
              label="Add'l Duty"
              options={ADD_BUCKETS}
              selected={f.addBucket}
              onChange={(next) => update('addBucket', next)}
            />
            <MultiFacet
              label='Score'
              options={SCORE_BUCKETS}
              selected={f.scoreBucket}
              onChange={(next) => update('scoreBucket', next)}
            />
            <MultiFacet
              label='Value'
              options={VALUE_BUCKETS}
              selected={f.valueBucket}
              onChange={(next) => update('valueBucket', next)}
            />
            <MultiFacet
              label='FTA'
              options={FTA_OPTS.map((option) => ({
                value: option,
                label: option === 'eligible' ? 'Eligible' : 'Ineligible',
              }))}
              selected={f.fta}
              onChange={(next) => update('fta', next)}
            />
            <MultiFacet
              label='Origin'
              options={origins.map((origin) => ({
                value: origin,
                label: origin,
              }))}
              selected={f.origin}
              onChange={(next) => update('origin', next)}
            />
            {savedViewDatabaseId && (
              <MultiFacet
                label='Ruleset'
                options={RULESET_FILTERS}
                selected={f.ruleset}
                onChange={(next) => update('ruleset', next)}
              />
            )}
            <MultiFacet
              label='Classification'
              options={CLASS_HEALTH}
              selected={f.classification}
              onChange={(next) => update('classification', next)}
            />
            <MultiFacet
              label='Issues'
              options={ISSUE_FILTERS}
              selected={f.issueType}
              onChange={(next) => update('issueType', next)}
            />
            {showDatabaseFilter && (
              <MultiFacet
                label='Database'
                options={databases.map((database) => ({
                  value: database.id,
                  label: database.name,
                }))}
                selected={f.database}
                onChange={(next) => update('database', next)}
              />
            )}
          </div>
        )}
      </div>

      {/* Active facet chips */}
      {activeCount > 0 && (
        <div className='ih-products-active-filters'>
          {f.q && <Chip label={`“${f.q}”`} onRemove={() => update('q', '')} />}
          {hierarchyFilter.map((v) => (
            <Chip
              key={'h' + v}
              label={`Hierarchy: ${v}`}
              onRemove={() => removeChip('hierarchy', v)}
            />
          ))}
          {f.risk.map((v) => (
            <Chip
              key={'r' + v}
              label={`Risk: ${v}`}
              onRemove={() => removeChip('risk', v)}
            />
          ))}
          {f.pga.map((v) => (
            <Chip
              key={'p' + v}
              label={`PGA: ${pgaProgramInfo(v).short}`}
              onRemove={() => removeChip('pga', v)}
            />
          ))}
          {f.tariff.map((v) => (
            <Chip
              key={'t' + v}
              label={`Tariff: ${v}`}
              onRemove={() => removeChip('tariff', v)}
            />
          ))}
          {f.addBucket.map((v) => (
            <Chip
              key={'ab' + v}
              label={`Add'l: ${
                ADD_BUCKETS.find((b) => b.value === v)?.label ?? v
              }`}
              onRemove={() => removeChip('addBucket', v)}
            />
          ))}
          {f.scoreBucket.map((v) => (
            <Chip
              key={'sb' + v}
              label={`Score: ${
                SCORE_BUCKETS.find((b) => b.value === v)?.label ?? v
              }`}
              onRemove={() => removeChip('scoreBucket', v)}
            />
          ))}
          {f.valueBucket.map((v) => (
            <Chip
              key={'value' + v}
              label={`Value: ${
                VALUE_BUCKETS.find((bucket) => bucket.value === v)?.label ?? v
              }`}
              onRemove={() => removeChip('valueBucket', v)}
            />
          ))}
          {f.fta.map((v) => (
            <Chip
              key={'f' + v}
              label={`FTA: ${v}`}
              onRemove={() => removeChip('fta', v)}
            />
          ))}
          {f.origin.map((v) => (
            <Chip
              key={'o' + v}
              label={`Origin: ${v}`}
              onRemove={() => removeChip('origin', v)}
            />
          ))}
          {f.ruleset.map((v) => (
            <Chip
              key={'rs' + v}
              label={
                RULESET_FILTERS.find((option) => option.value === v)?.label ?? v
              }
              onRemove={() => removeChip('ruleset', v)}
            />
          ))}
          {f.classification.map((v) => (
            <Chip
              key={'cl' + v}
              label={CLASS_HEALTH.find((c) => c.value === v)?.label ?? v}
              onRemove={() => removeChip('classification', v)}
            />
          ))}
          {f.issueType.map((v) => (
            <Chip
              key={'issue' + v}
              label={
                ISSUE_FILTERS.find((issue) => issue.value === v)?.label ?? v
              }
              onRemove={() => removeChip('issueType', v)}
            />
          ))}
          {f.database.map((v) => (
            <Chip
              key={'db' + v}
              label={`DB: ${databases.find((d) => d.id === v)?.name ?? v}`}
              onRemove={() => removeChip('database', v)}
            />
          ))}
          <button
            type='button'
            onClick={clearAll}
            className='ih-products-clear-filters'
          >
            Clear all
          </button>
        </div>
      )}

      {/* Summary + bulk actions */}
      <div className='flex items-center gap-3 flex-wrap'>
        <div className='text-xs text-muted-foreground'>
          Showing{' '}
          <span className='text-foreground font-semibold tabular-nums'>
            {serverSide && sorted.length > 0
              ? `${(page - 1) * pageSize + 1}–${
                  (page - 1) * pageSize + sorted.length
                }`
              : sorted.length}
          </span>{' '}
          of {serverSide ? totalProducts ?? 0 : products.length} products
        </div>
        {effectiveCount > 0 && (
          <>
            <div className='text-xs text-muted-foreground'>·</div>
            <div className='text-xs'>
              <span className='text-foreground font-semibold tabular-nums'>
                {effectiveCount}
              </span>{' '}
              selected
              {selectFilteredMode && (
                <span className='text-muted-foreground'> (all filtered)</span>
              )}
            </div>
            {savedViewDatabaseId && (
              <Dropdown
                open={bulkOpen}
                onOpenChange={(v) => {
                  if (!readOnly) setBulkOpen(v);
                }}
                trigger={
                  <button
                    className='btn-primary inline-flex items-center gap-1.5 h-7 px-3 rounded-md text-xs font-medium bg-[var(--action-color)] text-white hover:opacity-90'
                    disabled={readOnly}
                    title={readOnly ? 'Requires a Business license' : undefined}
                  >
                    {readOnly ? (
                      <Lock className='size-3.5' />
                    ) : (
                      <Settings2 className='size-3.5' />
                    )}{' '}
                    Bulk actions <ChevronDown className='size-3' />
                  </button>
                }
              >
                <div className='p-1 min-w-[220px] text-xs'>
                  <BulkItem
                    icon={PencilLine}
                    label='Edit field…'
                    onClick={() => openBulkAction('edit')}
                  />
                  <BulkItem
                    icon={Percent}
                    label='Manage tariff exclusions…'
                    onClick={() => openBulkAction('exclusion')}
                  />
                  <BulkItem
                    icon={Handshake}
                    label='Manage FTAs…'
                    onClick={() => openBulkAction('fta')}
                  />
                  <BulkItem
                    icon={Trash2}
                    label='Remove from database…'
                    onClick={() => openBulkAction('delete')}
                  />
                </div>
              </Dropdown>
            )}
            <button
              onClick={() => {
                setSelected(new Set());
                setSelectFilteredMode(false);
              }}
              className='text-xs text-muted-foreground hover:text-foreground'
            >
              Deselect
            </button>
          </>
        )}
        <div className='flex-1' />
        {onExportProducts && (
          <button
            type='button'
            onClick={() => onExportProducts(Array.from(effectiveSelected))}
            title={
              effectiveSelected.size > 0
                ? 'Exports only the selected rows'
                : 'Exports all rows matching the current filters'
            }
            className='inline-flex items-center gap-1.5 h-7 px-3 rounded-md text-xs border border-[var(--border-color)] bg-[var(--bg-surface)] hover:bg-accent'
          >
            <Download className='size-3.5' />{' '}
            {effectiveSelected.size > 0
              ? `Export Selected (${effectiveSelected.size.toLocaleString()})`
              : 'Export Table'}
          </button>
        )}
      </div>

      {/* "Select all N" banner when only visible page selected */}
      {someVisibleSelected &&
        !serverSide &&
        !selectFilteredMode &&
        selected.size < sorted.length && (
          <div className='rounded-md bg-[color-mix(in_oklab,var(--action-color)_8%,transparent)] border border-[var(--action-color)]/30 px-3 py-1.5 text-xs flex items-center gap-2'>
            <span>{selected.size} on this view selected.</span>
            <button
              onClick={() => setSelectFilteredMode(true)}
              className='font-semibold text-[var(--action-color)] hover:underline'
            >
              Select all {sorted.length} matching the current filters
            </button>
          </div>
        )}

      <div className='elms-table-wrapper'>
        <table className='w-full text-sm ih-products-table'>
          <thead>
            <tr className='ih-products-table-header'>
              <th className='w-6 px-2 py-2'></th>
              <th className='w-8 px-2 py-2'>
                <input
                  type='checkbox'
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el)
                      el.indeterminate =
                        !allVisibleSelected && someVisibleSelected;
                  }}
                  onChange={toggleSelectAllVisible}
                  className='rounded border-[var(--border-color)] cursor-pointer'
                  aria-label='Select all visible'
                />
              </th>
              <SortableTh
                label='Item'
                k='name'
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={toggleSort}
              />
              {showDatabaseColumn && visibleCol('database') && (
                <th className='text-left px-3 py-2'>Database</th>
              )}
              {visibleCol('hts') && (
                <SortableTh
                  label='HTS'
                  k='hts'
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
              )}
              {visibleCol('origin') && (
                <SortableTh
                  label='Origin'
                  k='origin'
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
              )}
              {visibleCol('value') && (
                <SortableTh
                  label='Value'
                  k='value'
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  align='right'
                />
              )}
              {visibleCol('baseDuty') && (
                <SortableTh
                  label='Base Duty'
                  k='baseDuty'
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  align='right'
                />
              )}
              {visibleCol('addDuty') && (
                <SortableTh
                  label="Add'l Tariff"
                  k='addDuty'
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  align='right'
                />
              )}
              {visibleCol('flags') && (
                <SortableTh
                  label='Flags'
                  k='flags'
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
              )}
              {visibleCol('score') && (
                <SortableTh
                  label='Score'
                  k='score'
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  align='right'
                />
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => {
              const isOpen = expanded.has(p.id);
              const sup = IMPORT_ENTITIES.find((e) => e.id === p.supplierId);
              const db = databases.find((d) => d.id === p.importDatabaseId);
              const addTotal = additionalDutyFor(p);
              const baseRate = baseDutyForProduct(p);
              const isSelected = effectiveSelected.has(p.id);
              return (
                <Fragment key={p.id}>
                  <tr
                    className={cn(
                      'elms-row',
                      isSelected &&
                        'bg-[color-mix(in_oklab,var(--action-color)_5%,transparent)]',
                    )}
                  >
                    <td
                      className={cn(
                        'px-2 py-2 text-muted-foreground',
                        !!p.childProductCount && 'cursor-pointer',
                      )}
                      onClick={() => toggleExpand(p)}
                    >
                      {!p.childProductCount ? null : isOpen ? (
                        <ChevronDown className='size-4' />
                      ) : (
                        <ChevronRight className='size-4' />
                      )}
                    </td>
                    <td className='px-2 py-2'>
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={() => toggleSelected(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        className='rounded border-[var(--border-color)] cursor-pointer'
                        aria-label={`Select ${p.name}`}
                      />
                    </td>
                    <td className='px-3 py-2'>
                      <div className='flex items-center gap-2'>
                        {renderProductLink({
                          id: p.id,
                          className:
                            'font-medium hover:text-[var(--action-color)]',
                          children: p.name,
                        })}
                        <HierarchyBadge hierarchyType={p.hierarchyType} />
                        {p.changedByRuleset && (
                          <span
                            className='ih-ruleset-marker'
                            title='Changed by the active ruleset'
                            aria-label='Changed by the active ruleset'
                          >
                            <ShieldCheck size={16} />
                          </span>
                        )}
                      </div>
                      <div className='text-[10px] text-muted-foreground font-mono'>
                        {p.sku}
                      </div>
                      {!!p.issueTypes?.length && (
                        <div className='ih-product-issue-indicators'>
                          {p.issueTypes.slice(0, 2).map((issueType) => (
                            <span
                              key={`${p.id}-${issueType}`}
                              className={cn(
                                'ih-product-issue-indicator',
                                issueType === 'hts_review_required'
                                  ? 'is-danger'
                                  : issueType === 'tariff_change'
                                  ? 'is-info'
                                  : 'is-warning',
                              )}
                            >
                              {ISSUE_FILTERS.find(
                                (option) => option.value === issueType,
                              )?.label ?? issueType}
                            </span>
                          ))}
                          {p.issueTypes.length > 2 && (
                            <span className='ih-product-issue-more'>
                              +{p.issueTypes.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    {showDatabaseColumn && visibleCol('database') && (
                      <td className='px-3 py-2 text-xs truncate max-w-[180px]'>
                        {db?.name ?? '—'}
                      </td>
                    )}
                    {visibleCol('hts') && (
                      <td className='px-3 py-2 font-mono text-xs'>
                        {p.htsCode ? (
                          isHtsInvalid(p) ? (
                            <span className='text-[var(--warning)]'>
                              {p.htsCode}
                            </span>
                          ) : (
                            p.htsCode
                          )
                        ) : (
                          <span className='text-[var(--danger)]'>
                            — missing
                          </span>
                        )}
                      </td>
                    )}
                    {visibleCol('origin') && (
                      <td className='px-3 py-2 text-xs'>
                        {p.countryOfOrigin || (
                          <span className='text-[var(--danger)]'>
                            — missing
                          </span>
                        )}
                      </td>
                    )}
                    {visibleCol('value') && (
                      <td className='px-3 py-2 text-right text-xs tabular-nums'>
                        {formatProductValue(productValueForTable(p))}
                      </td>
                    )}
                    {visibleCol('baseDuty') && (
                      <td className='px-3 py-2 text-right text-xs tabular-nums'>
                        {p.baseDutyRate !== undefined ? (
                          <span
                            className={
                              p.baseDutyRate === 'Free' ||
                              p.baseDutyRate === '0%'
                                ? 'text-muted-foreground'
                                : 'text-[var(--warning)]'
                            }
                          >
                            {p.baseDutyRate}
                          </span>
                        ) : p.databaseProductId ? (
                          <span className='text-muted-foreground'>—</span>
                        ) : (
                          <span className='text-muted-foreground'>
                            {baseRate.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    )}
                    {visibleCol('addDuty') && (
                      <td className='px-3 py-2 text-right text-xs tabular-nums font-semibold'>
                        {p.additionalTariffDisplay !== undefined ? (
                          <span
                            className={
                              p.additionalTariffDisplay === '0.00%'
                                ? 'text-muted-foreground'
                                : 'text-[var(--warning)]'
                            }
                          >
                            {(displayedPercent(p.additionalTariffDisplay) ??
                              0) > 0
                              ? `+${p.additionalTariffDisplay.replace(
                                  /^\+/,
                                  '',
                                )}`
                              : p.additionalTariffDisplay}
                          </span>
                        ) : (
                          <span
                            className={cn(
                              addTotal === 0
                                ? 'text-muted-foreground'
                                : addTotal <= 10
                                ? 'text-[var(--action-color)]'
                                : addTotal <= 25
                                ? 'text-[var(--warning)]'
                                : 'text-[var(--danger)]',
                            )}
                          >
                            {addTotal > 0 ? `+${addTotal.toFixed(1)}%` : '—'}
                          </span>
                        )}
                      </td>
                    )}
                    {visibleCol('flags') && (
                      <td className='px-3 py-2'>
                        <ProgramFlagsCell product={p} />
                      </td>
                    )}
                    {visibleCol('score') && (
                      <td className='px-3 py-2 text-right'>
                        <ScorePill
                          score={p.complianceScore}
                          assessed={p.complianceAssessed !== false}
                        />
                      </td>
                    )}
                  </tr>
                  {isOpen && (
                    <tr className='bg-[var(--bg-surface)]'>
                      <td colSpan={totalCols} className='px-4 py-4'>
                        <ExpandedRow
                          product={p}
                          supplierName={sup?.name}
                          renderProductLink={renderProductLink}
                          relatedById={relatedProducts}
                          onLoadRelated={loadRelated}
                          databaseStructure={
                            p.databaseStructure ?? databaseStructure
                          }
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td
                  colSpan={totalCols}
                  className='px-6 py-12 text-center text-sm text-muted-foreground'
                >
                  <Filter className='size-6 inline-block mb-2 opacity-50' />
                  <div>No products match these filters.</div>
                  <button
                    onClick={clearAll}
                    className='mt-2 text-xs text-[var(--action-color)] hover:underline'
                  >
                    Clear filters
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className='ih-products-page-loading' aria-live='polite'>
          <span className='ih-spinner' /> Loading products…
        </div>
      )}
      {serverSide && (totalProducts ?? 0) > pageSize && (
        <div className='ih-priority-pagination' aria-label='Product pages'>
          <Button
            variant='outline'
            size='sm'
            disabled={loading || page <= 1}
            onClick={() => onPageChange?.(Math.max(1, page - 1))}
          >
            Previous
          </Button>
          <span className='ih-text-xs ih-text-muted ih-num'>
            Page {page} of {Math.ceil((totalProducts ?? 0) / pageSize)}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={
              loading || page >= Math.ceil((totalProducts ?? 0) / pageSize)
            }
            onClick={() => onPageChange?.(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <SaveImportViewDialog
        open={savedViewDialogMode !== null}
        mode={savedViewDialogMode ?? 'create'}
        initialName={savedViewDialogTarget?.name}
        summary={savedViewSummary}
        visibleColumnCount={visibleSavedColumnCount}
        saving={savedViewSaving}
        error={savedViewDialogError}
        onClose={closeSavedViewDialog}
        onSave={saveSavedViewDialog}
      />

      <Dialog
        open={bulkAction !== null}
        onOpenChange={(open) => {
          if (!open) closeBulkAction();
        }}
      >
        <DialogContent
          size='md'
          className={
            bulkAction === 'exclusion' || bulkAction === 'fta'
              ? 'ih-bulk-exclusion-dialog'
              : undefined
          }
          onClose={closeBulkAction}
        >
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'delete'
                ? 'Remove selected products?'
                : bulkAction === 'exclusion'
                ? `Manage exclusions on ${bulkExclusionTargetCount} selected product${
                    bulkExclusionTargetCount === 1 ? '' : 's'
                  }`
                : bulkAction === 'fta'
                ? `Manage FTAs on ${bulkFtaTargetCount} selected product${
                    bulkFtaTargetCount === 1 ? '' : 's'
                  }`
                : `Edit field on ${effectiveCount} selected product${
                    effectiveCount === 1 ? '' : 's'
                  }`}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {bulkAction === 'delete' ? (
              <p className='ih-saved-view-delete-copy'>
                {effectiveCount} selected product
                {effectiveCount === 1 ? '' : 's'} will be removed from this
                database. Products associated with other databases will not be
                affected.
              </p>
            ) : bulkAction === 'exclusion' ? (
              <div className='ih-col ih-gap-3'>
                <p className='ih-bulk-exclusion-copy'>
                  Options are based on the latest tariff results for the
                  selected products. Inapplicable products will never be
                  changed.
                </p>
                {bulkExclusionLoading ? (
                  <div className='ih-bulk-exclusion-notice'>
                    Loading applicable tariff exclusions…
                  </div>
                ) : bulkExclusionError ? (
                  <div className='ih-bulk-exclusion-notice ih-bulk-exclusion-notice-error'>
                    {bulkExclusionError}
                  </div>
                ) : bulkExclusionOptions.length === 0 ? (
                  <div className='ih-bulk-exclusion-notice'>
                    No applicable or previously applied tariff exclusions were
                    found. Screen the selected products first and confirm they
                    have potential exclusions.
                  </div>
                ) : (
                  <>
                    <label className='ih-col ih-gap-2'>
                      <span className='ih-label'>Action</span>
                      <select
                        className='ih-select ih-bulk-exclusion-select'
                        value={bulkExclusionMode}
                        onChange={(event) => {
                          const mode = event.target.value as 'apply' | 'remove';
                          setBulkExclusionMode(mode);
                          const current = bulkExclusionOptions.find(
                            (option) => option.code === bulkExclusionCode,
                          );
                          const currentCanChange =
                            mode === 'apply'
                              ? (current?.applyUpdateCount ?? 0) > 0
                              : (current?.appliedCount ?? 0) > 0;
                          if (!currentCanChange) {
                            const preferred = bulkExclusionOptions.find(
                              (option) =>
                                mode === 'apply'
                                  ? option.applyUpdateCount > 0
                                  : option.appliedCount > 0,
                            );
                            if (preferred) setBulkExclusionCode(preferred.code);
                          }
                        }}
                        disabled={bulkSaving}
                      >
                        <option value='apply'>Apply exclusion</option>
                        <option value='remove'>Remove exclusion</option>
                      </select>
                    </label>
                    <label className='ih-col ih-gap-2'>
                      <span className='ih-label'>Tariff exclusion</span>
                      <select
                        className='ih-select ih-bulk-exclusion-select'
                        value={bulkExclusionCode}
                        onChange={(event) =>
                          setBulkExclusionCode(event.target.value)
                        }
                        disabled={bulkSaving}
                      >
                        {bulkExclusionOptions.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.codePretty}
                            {option.label ? ` — ${option.label}` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selectedBulkExclusionOption && (
                      <div className='ih-bulk-exclusion-summary'>
                        <div className='ih-bulk-exclusion-summary-title'>
                          {selectedBulkExclusionOption.label ||
                            selectedBulkExclusionOption.codePretty}
                        </div>
                        {selectedBulkExclusionOption.question && (
                          <div className='ih-bulk-exclusion-summary-copy'>
                            {selectedBulkExclusionOption.question}
                          </div>
                        )}
                        {selectedBulkExclusionOption.note && (
                          <div className='ih-bulk-exclusion-summary-note'>
                            {selectedBulkExclusionOption.note}
                          </div>
                        )}
                        <div className='ih-bulk-exclusion-metrics'>
                          <span>
                            <span>Applicable to</span>
                            <strong>
                              {selectedBulkExclusionOption.applicableCount}
                            </strong>
                          </span>
                          <span>
                            <span>Already applied to</span>
                            <strong>
                              {selectedBulkExclusionOption.appliedCount}
                            </strong>
                          </span>
                          {selectedBulkExclusionOption.rateDisplay && (
                            <span>
                              <span>Rate</span>
                              <strong>
                                {selectedBulkExclusionOption.rateDisplay}
                              </strong>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {bulkExclusionScreenedCount < bulkExclusionTargetCount && (
                      <div className='ih-bulk-exclusion-warning'>
                        {bulkExclusionTargetCount - bulkExclusionScreenedCount}{' '}
                        selected product
                        {bulkExclusionTargetCount -
                          bulkExclusionScreenedCount ===
                        1
                          ? ''
                          : 's'}{' '}
                        do not have tariff results and will be skipped.
                      </div>
                    )}
                    <div className='ih-bulk-exclusion-footnote'>
                      {bulkExclusionUpdateCount} product
                      {bulkExclusionUpdateCount === 1 ? '' : 's'} will be
                      recalculated. Other exclusions and FTA selections will be
                      preserved.
                    </div>
                  </>
                )}
              </div>
            ) : bulkAction === 'fta' ? (
              <div className='ih-col ih-gap-3'>
                <p className='ih-bulk-exclusion-copy'>
                  Applicable FTAs come from the HTS special rate and country of
                  origin of each product. Products without an applicable FTA
                  will never be changed.
                </p>
                {bulkFtaLoading ? (
                  <div className='ih-bulk-exclusion-notice'>
                    Checking applicable FTAs…
                  </div>
                ) : bulkFtaError ? (
                  <div className='ih-bulk-exclusion-notice ih-bulk-exclusion-notice-error'>
                    {bulkFtaError}
                  </div>
                ) : bulkFtaOptions ? (
                  <>
                    <label className='ih-col ih-gap-2'>
                      <span className='ih-label'>Action</span>
                      <select
                        className='ih-select ih-bulk-exclusion-select'
                        value={bulkFtaMode}
                        onChange={(event) =>
                          setBulkFtaMode(
                            event.target.value as 'apply' | 'remove',
                          )
                        }
                        disabled={bulkSaving}
                      >
                        <option value='apply'>
                          Turn on all applicable FTAs
                        </option>
                        <option value='remove'>Turn off all FTAs</option>
                      </select>
                    </label>
                    {bulkFtaMode === 'apply' ? (
                      bulkFtaOptions.options.length === 0 ? (
                        <div className='ih-bulk-exclusion-notice'>
                          None of the selected products have an applicable FTA.
                        </div>
                      ) : (
                        <>
                          <div className='ih-bulk-exclusion-summary'>
                            <div className='ih-bulk-exclusion-summary-title'>
                              Applicable FTAs
                            </div>
                            <ul className='ih-bulk-fta-list'>
                              {bulkFtaOptions.options.map((option) => (
                                <li key={option.code}>
                                  <span className='ih-bulk-fta-name'>
                                    {option.name}
                                    <span className='ih-bulk-fta-code'>
                                      {option.code}
                                    </span>
                                  </span>
                                  <span className='ih-bulk-fta-counts'>
                                    Applies to{' '}
                                    <strong>{option.applicableCount}</strong>
                                    {' · '}On for{' '}
                                    <strong>{option.enabledCount}</strong>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {bulkFtaOptions.applyUpdateCount === 0 && (
                            <div className='ih-bulk-exclusion-notice'>
                              Every applicable FTA is already on for the
                              selected products.
                            </div>
                          )}
                        </>
                      )
                    ) : (
                      bulkFtaOptions.removeUpdateCount === 0 && (
                        <div className='ih-bulk-exclusion-notice'>
                          None of the selected products have an FTA turned on.
                        </div>
                      )
                    )}
                    {bulkFtaOptions.unavailableCount > 0 && (
                      <div className='ih-bulk-exclusion-warning'>
                        {bulkFtaOptions.unavailableCount} selected product
                        {bulkFtaOptions.unavailableCount === 1 ? '' : 's'}{' '}
                        {bulkFtaOptions.unavailableCount === 1 ? 'has' : 'have'}{' '}
                        no tariff calculation (missing or invalid HTS) and will
                        be skipped.
                      </div>
                    )}
                    {bulkFtaOptions.failedCount > 0 && (
                      <div className='ih-bulk-exclusion-warning'>
                        {bulkFtaOptions.failedCount} selected product
                        {bulkFtaOptions.failedCount === 1 ? '' : 's'} could not
                        be checked and may fail to update.
                      </div>
                    )}
                    <div className='ih-bulk-exclusion-footnote'>
                      {bulkFtaUpdateCount} product
                      {bulkFtaUpdateCount === 1 ? '' : 's'} will be
                      recalculated. Tariff exclusions will be preserved.
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              <div className='ih-col ih-gap-2'>
                <label className='ih-col ih-gap-2'>
                  <span className='ih-label'>Field</span>
                  <select
                    className='ih-select'
                    value={bulkEditField}
                    onChange={(event) => {
                      setBulkEditField(event.target.value as BulkEditFieldKey);
                      setBulkEditValue('');
                    }}
                    disabled={bulkSaving}
                  >
                    {BULK_EDIT_FIELDS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className='ih-col ih-gap-2'>
                  <span className='ih-label'>New value</span>
                  {bulkEditField === 'country_of_origin' ? (
                    <select
                      className='ih-select'
                      value={bulkEditValue}
                      onChange={(event) => setBulkEditValue(event.target.value)}
                      disabled={bulkSaving}
                    >
                      <option value='' disabled>
                        Select country
                      </option>
                      {countries.map(({ name, iso2 }) => (
                        <option key={iso2} value={iso2}>
                          {name} ({iso2})
                        </option>
                      ))}
                    </select>
                  ) : bulkEditField === 'category' ? (
                    <select
                      className='ih-select'
                      value={bulkEditValue}
                      onChange={(event) => setBulkEditValue(event.target.value)}
                      disabled={bulkSaving}
                    >
                      <option value=''>Clear category</option>
                      {productCategories.map((option) => (
                        <option
                          key={option.id ?? option.name}
                          value={option.name}
                        >
                          {option.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={bulkEditValue}
                      onChange={(event) => setBulkEditValue(event.target.value)}
                      placeholder='Leave blank to clear'
                      disabled={bulkSaving}
                    />
                  )}
                </label>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant='outline'
              className={
                bulkAction === 'exclusion' || bulkAction === 'fta'
                  ? 'ih-bulk-exclusion-cancel'
                  : undefined
              }
              disabled={bulkSaving}
              onClick={closeBulkAction}
            >
              Cancel
            </Button>
            <Button
              variant={bulkAction === 'delete' ? 'danger' : 'primary'}
              locked={readOnly}
              disabled={
                bulkSaving ||
                (bulkAction === 'edit' &&
                  bulkEditField === 'country_of_origin' &&
                  !bulkEditValue) ||
                (bulkAction === 'exclusion' &&
                  (bulkExclusionLoading ||
                    Boolean(bulkExclusionError) ||
                    !bulkExclusionCode ||
                    bulkExclusionUpdateCount === 0)) ||
                (bulkAction === 'fta' &&
                  (bulkFtaLoading ||
                    Boolean(bulkFtaError) ||
                    bulkFtaUpdateCount === 0))
              }
              onClick={() => void confirmBulkAction()}
            >
              {bulkAction === 'delete'
                ? bulkSaving
                  ? 'Removing…'
                  : 'Remove products'
                : bulkAction === 'exclusion'
                ? bulkSaving
                  ? 'Recalculating tariffs…'
                  : bulkExclusionMode === 'apply'
                  ? `Apply to ${bulkExclusionUpdateCount}`
                  : `Remove from ${bulkExclusionUpdateCount}`
                : bulkAction === 'fta'
                ? bulkSaving
                  ? 'Recalculating tariffs…'
                  : bulkFtaMode === 'apply'
                  ? `Turn on for ${bulkFtaUpdateCount}`
                  : `Turn off for ${bulkFtaUpdateCount}`
                : bulkSaving
                ? bulkProgress
                  ? `Updating… (${bulkProgress.done} / ${bulkProgress.total})`
                  : 'Updating…'
                : 'Update field'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={savedViewDeleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !savedViewDeleting) setSavedViewDeleteTarget(null);
        }}
      >
        <DialogContent
          size='md'
          onClose={() => !savedViewDeleting && setSavedViewDeleteTarget(null)}
        >
          <DialogHeader>
            <DialogTitle>Delete saved view?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className='ih-saved-view-delete-copy'>
              “{savedViewDeleteTarget?.name}” will be removed for everyone with
              access to this database. This cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              variant='outline'
              disabled={savedViewDeleting}
              onClick={() => setSavedViewDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant='danger'
              locked={readOnly}
              disabled={savedViewDeleting}
              onClick={deleteSavedView}
            >
              {savedViewDeleting ? 'Deleting…' : 'Delete view'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ============================== Sub components ============================== */

const SortableTh: React.FC<{
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'right';
}> = ({ label, k, sortKey, sortDir, onSort, align }) => {
  const active = sortKey === k;
  return (
    <th
      className={cn(
        'px-3 py-2',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      <button
        onClick={() => onSort(k)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground transition-colors',
          align === 'right' && 'flex-row-reverse',
        )}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUp className='size-3' />
          ) : (
            <ChevronDown className='size-3' />
          )
        ) : (
          <ArrowUpDown className='size-3 opacity-40' />
        )}
      </button>
    </th>
  );
};

const MultiFacet: React.FC<{
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const toggle = (v: string) =>
    onChange(
      selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v],
    );
  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type='button'
          className={cn(
            'ih-products-facet-button',
            selected.length > 0 && 'is-active',
          )}
        >
          <span>{label}</span>
          {selected.length > 0 && (
            <span className='ih-products-facet-count'>{selected.length}</span>
          )}
          <ChevronDown size={14} />
        </button>
      }
    >
      <div className='ih-products-facet-menu-content'>
        {options.map((o) => {
          const checked = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type='button'
              onClick={(e) => {
                e.preventDefault();
                toggle(o.value);
              }}
              className='ih-products-facet-option'
            >
              <span
                className={cn(
                  'ih-products-facet-check',
                  checked && 'is-checked',
                )}
              >
                {checked && '✓'}
              </span>
              <span className='truncate'>{o.label}</span>
            </button>
          );
        })}
      </div>
    </Dropdown>
  );
};

/** Tiny click-outside dropdown wrapper. */
const Dropdown: React.FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: 'left' | 'right';
}> = ({ open, onOpenChange, trigger, children, align = 'left' }) => {
  return (
    <div className='ih-products-dropdown'>
      <div onClick={() => onOpenChange(!open)}>{trigger}</div>
      {open && (
        <>
          <div
            className='fixed inset-0 z-10'
            onClick={() => onOpenChange(false)}
          />
          <div
            className={cn(
              'ih-products-dropdown-menu',
              align === 'right' && 'is-right',
            )}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
};

const Chip: React.FC<{ label: string; onRemove: () => void }> = ({
  label,
  onRemove,
}) => {
  return (
    <span className='ih-products-active-chip'>
      <span>{label}</span>
      <button
        onClick={onRemove}
        className='ih-products-active-chip-remove'
        aria-label='Remove filter'
      >
        <X className='size-3' />
      </button>
    </span>
  );
};

const BulkItem: React.FC<{ icon: any; label: string; onClick: () => void }> = ({
  icon: Icon,
  label,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className='w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-accent text-left'
    >
      <Icon className='size-3.5 text-muted-foreground' />
      <span>{label}</span>
    </button>
  );
};

const ScorePill: React.FC<{ score: number; assessed?: boolean }> = ({
  score,
  assessed = true,
}) => {
  if (!assessed) {
    return (
      <span className='text-[11px] font-medium text-muted-foreground'>
        Not assessed
      </span>
    );
  }
  const cls =
    score >= 85
      ? 'badge-success'
      : score >= 65
      ? 'badge-info'
      : score >= 45
      ? 'badge-warning'
      : 'badge-danger';
  return (
    <span
      className={`inline-flex items-center text-[11px] font-semibold tabular-nums rounded-full px-2 py-0.5 ${cls}`}
    >
      {score}
    </span>
  );
};

const ProgramFlagsCell: React.FC<{ product: ImportProduct }> = ({
  product,
}) => {
  const flags = programFlagsFor(product);
  if (flags.length === 0) {
    return <span className='ih-text-muted ih-text-xs'>—</span>;
  }

  return (
    <div
      className='ih-product-program-flags'
      aria-label={flags.map((flag) => flag.title).join(', ')}
    >
      {flags.map((flag) => (
        <span
          key={flag.key}
          className={cn('ih-product-program-flag', `is-${flag.tone}`)}
          title={flag.title}
        >
          {flag.label}
        </span>
      ))}
    </div>
  );
};

const HierarchyBadge: React.FC<{ hierarchyType?: string }> = ({
  hierarchyType,
}) => {
  const label =
    hierarchyType === 'Assembly'
      ? 'ASM'
      : hierarchyType === 'Sub-Assembly'
      ? 'SUB'
      : hierarchyType === 'Component'
      ? 'BOM'
      : null;
  if (!label) return null;
  return (
    <span
      className={cn(
        'ih-product-program-flag',
        hierarchyType === 'Assembly' && 'is-fta',
        hierarchyType === 'Sub-Assembly' && 'is-tariff',
      )}
      title={hierarchyType}
    >
      {label}
    </span>
  );
};

type RelatedProductsState = {
  loading: boolean;
  error?: string;
  products: ImportProduct[];
};

const RelatedProductRows: React.FC<{
  products: ImportProduct[];
  depth: number;
  ancestorIds: string[];
  showRelationship: boolean;
  relatedById: Record<string, RelatedProductsState>;
  onLoadRelated: (product: ImportProduct) => void;
  renderProductLink: RenderProductLink;
}> = ({
  products,
  depth,
  ancestorIds,
  showRelationship,
  relatedById,
  onLoadRelated,
  renderProductLink,
}) => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const columnCount = showRelationship ? 5 : 2;
  return (
    <>
      {products.map((child) => {
        const expandable =
          showRelationship &&
          Boolean(child.childProductCount) &&
          !ancestorIds.includes(child.id);
        const isOpen = expandable && open.has(child.id);
        const nested = relatedById[child.id];
        return (
          <Fragment key={`${ancestorIds.join('/')}/${child.id}`}>
            <tr className='border-t border-[var(--edge-soft)]'>
              <td className='px-3 py-2'>
                <div
                  className='flex items-center gap-2'
                  style={{ paddingLeft: depth * 18 }}
                >
                  {expandable ? (
                    <button
                      type='button'
                      aria-label={isOpen ? 'Collapse' : 'Expand'}
                      aria-expanded={isOpen}
                      className='text-muted-foreground'
                      onClick={() => {
                        setOpen((current) => {
                          const next = new Set(current);
                          if (next.has(child.id)) next.delete(child.id);
                          else next.add(child.id);
                          return next;
                        });
                        if (!isOpen) onLoadRelated(child);
                      }}
                    >
                      {isOpen ? (
                        <ChevronDown className='size-3.5' />
                      ) : (
                        <ChevronRight className='size-3.5' />
                      )}
                    </button>
                  ) : (
                    showRelationship && <span className='inline-block w-3.5' />
                  )}
                  {renderProductLink({
                    id: child.id,
                    className: 'font-medium hover:text-[var(--action-color)]',
                    children: child.name,
                  })}
                  <HierarchyBadge hierarchyType={child.hierarchyType} />
                </div>
                <div
                  className='text-[10px] text-muted-foreground font-mono'
                  style={{
                    paddingLeft: depth * 18 + (showRelationship ? 22 : 0),
                  }}
                >
                  {child.sku || '—'}
                </div>
              </td>
              <td className='px-3 py-2 font-mono'>
                {child.htsCode ? (
                  isHtsInvalid(child) ? (
                    <span className='text-[var(--warning)]'>
                      {child.htsCode}
                    </span>
                  ) : (
                    child.htsCode
                  )
                ) : (
                  <span className='text-[var(--danger)]'>— missing</span>
                )}
              </td>
              {showRelationship && (
                <>
                  <td className='px-3 py-2 text-right tabular-nums'>
                    {child.relationshipQuantity ?? '—'}
                  </td>
                  <td className='px-3 py-2'>
                    {child.relationshipUnitOfMeasure || '—'}
                  </td>
                  <td className='px-3 py-2 text-right tabular-nums'>
                    {child.relationshipUnitValue == null
                      ? '—'
                      : formatCurrency(child.relationshipUnitValue)}
                  </td>
                </>
              )}
            </tr>
            {isOpen &&
              (nested?.products.length ? (
                <RelatedProductRows
                  products={nested.products}
                  depth={depth + 1}
                  ancestorIds={[...ancestorIds, child.id]}
                  showRelationship={showRelationship}
                  relatedById={relatedById}
                  onLoadRelated={onLoadRelated}
                  renderProductLink={renderProductLink}
                />
              ) : (
                <tr>
                  <td
                    colSpan={columnCount}
                    className={cn(
                      'px-3 py-2 text-xs',
                      nested?.error
                        ? 'text-[var(--low-color)]'
                        : 'text-muted-foreground',
                    )}
                    style={{ paddingLeft: 12 + (depth + 1) * 18 }}
                  >
                    {!nested || nested.loading
                      ? 'Loading components…'
                      : nested.error ?? 'No components found.'}
                  </td>
                </tr>
              ))}
          </Fragment>
        );
      })}
    </>
  );
};

const ExpandedRow: React.FC<{
  product: ImportProduct;
  supplierName?: string;
  renderProductLink: RenderProductLink;
  relatedById: Record<string, RelatedProductsState>;
  onLoadRelated: (product: ImportProduct) => void;
  databaseStructure: string;
}> = ({
  product: p,
  supplierName,
  renderProductLink,
  relatedById,
  onLoadRelated,
  databaseStructure,
}) => {
  const totalDutyPct = additionalDutyFor(p);
  const annualDuty = p.declaredValue * (totalDutyPct / 100);
  const related = relatedById[p.id];
  const isHtsGroup = databaseStructure === 'hts-code-grouping';
  return (
    <div className='space-y-4'>
      <div>
        <div className='text-[10px] uppercase tracking-wider text-muted-foreground mb-2'>
          {isHtsGroup
            ? `HTS group · products sharing ${p.htsCode || 'this HTS code'}`
            : 'Bill of materials'}
        </div>
        {!related || related.loading ? (
          <div className='text-xs text-muted-foreground'>
            Loading related products…
          </div>
        ) : related.error ? (
          <div className='text-xs text-[var(--low-color)]'>{related.error}</div>
        ) : related.products.length ? (
          <div className='overflow-x-auto rounded border border-[var(--edge-soft)]'>
            <table className='w-full text-xs'>
              <thead className='bg-[var(--bg-surface)] text-muted-foreground'>
                <tr>
                  <th className='px-3 py-2 text-left'>Product</th>
                  <th className='px-3 py-2 text-left'>HTS</th>
                  {!isHtsGroup && (
                    <>
                      <th className='px-3 py-2 text-right'>Quantity</th>
                      <th className='px-3 py-2 text-left'>UOM</th>
                      <th className='px-3 py-2 text-right'>Unit value</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                <RelatedProductRows
                  products={related.products}
                  depth={0}
                  ancestorIds={[p.id]}
                  showRelationship={!isHtsGroup}
                  relatedById={relatedById}
                  onLoadRelated={onLoadRelated}
                  renderProductLink={renderProductLink}
                />
              </tbody>
            </table>
          </div>
        ) : (
          <div className='text-xs text-muted-foreground'>
            {isHtsGroup
              ? 'No other products share this HTS code.'
              : 'No components found.'}
          </div>
        )}
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        <div>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground mb-1'>
            PGA agency breakdown
          </div>
          {(p.pgaPrograms ?? p.pgaFlags).length === 0 ? (
            <p className='text-xs text-muted-foreground'>
              No PGA agencies flagged.
            </p>
          ) : (
            <div className='flex flex-wrap gap-1'>
              {p.pgaPrograms
                ? p.pgaPrograms.map((code) => {
                    const program = pgaProgramInfo(code);
                    return (
                      <span
                        key={code}
                        title={program.name}
                        className='text-[10px] font-mono font-semibold rounded px-2 py-0.5 badge-info'
                      >
                        {program.short}
                      </span>
                    );
                  })
                : p.pgaFlags.map((a) => (
                    <span
                      key={a}
                      className='text-[10px] font-mono font-semibold rounded px-2 py-0.5 badge-info'
                    >
                      {a}
                    </span>
                  ))}
            </div>
          )}
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground mt-3 mb-1'>
            Supplier
          </div>
          <div className='text-xs'>{supplierName ?? '—'}</div>
        </div>

        <div>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground mb-1'>
            Tariff line
          </div>
          <div className='space-y-1 text-xs'>
            <KV
              k='Section 301'
              v={p.section301 ? `${p.section301Rate}%` : '—'}
            />
            <KV
              k='Section 232'
              v={p.section232 ? `${p.section232Rate}%` : '—'}
            />
            <KV
              k='Additional duty'
              v={totalDutyPct > 0 ? `${totalDutyPct.toFixed(2)}%` : '—'}
            />
            <KV k='Total over base' v={`${totalDutyPct.toFixed(1)}%`} />
            <KV k='Est. annual' v={formatCurrency(annualDuty)} />
          </div>
        </div>

        <div className='flex flex-col gap-3'>
          <div>
            <div className='text-[10px] uppercase tracking-wider text-muted-foreground mb-1'>
              Screening risk
            </div>
            <div className='space-y-1 text-xs'>
              <KV
                k='Compliance score'
                v={
                  p.complianceAssessed === false
                    ? 'Not assessed'
                    : String(p.complianceScore)
                }
              />
              <KV k='Risk' v={p.riskLevel} />
              <KV
                k='FTA'
                v={
                  p.ftaEvaluated === false
                    ? 'Not evaluated'
                    : p.ftaEligible
                    ? `${p.ftaPrograms?.join(', ') || p.fta} enabled`
                    : p.fta === 'none'
                    ? '—'
                    : `${p.fta} (unconfirmed)`
                }
              />
            </div>
          </div>
          {renderProductLink({
            id: p.id,
            className:
              'inline-flex self-start items-center gap-1 text-xs font-medium text-[var(--action-color)] hover:underline',
            children: (
              <>
                Open product detail <ChevronRight className='size-3' />
              </>
            ),
          })}
        </div>
      </div>
    </div>
  );
};

const KV: React.FC<{ k: string; v: string }> = ({ k, v }) => {
  return (
    <div className='flex items-baseline justify-between gap-2'>
      <span className='text-muted-foreground'>{k}</span>
      <span className='font-medium text-right'>{v}</span>
    </div>
  );
};
